
import { FC } from 'react';
import { Product, UsageRecord, StockUpdate } from "@/lib/types";
import { GroupAnalytics } from "@/components/analytics/group";
import ProductStats from "@/components/analytics/ProductStats";
import OverallSummary from "@/components/analytics/OverallSummary";
import ProductCharts from "@/components/analytics/ProductCharts";
import ProductComparison from "@/components/analytics/ProductComparison";
import { prepareProductUsageData, prepareStockLevelHistory, calculateProductStats, calculateTotalStats } from "@/utils/analytics/dataUtils";

interface AnalyticsContentProps {
  selectedGroupId: string;
  groups: any[];
  products: Product[];
  usageRecords: UsageRecord[];
  timeRange: "7days" | "30days" | "90days";
  selectedProduct: Product | undefined;
  stockUpdates: StockUpdate[];
  filteredUsageRecords: UsageRecord[];
  filteredStockUpdates: StockUpdate[];
}

const AnalyticsContent: FC<AnalyticsContentProps> = ({
  selectedGroupId,
  groups,
  products,
  usageRecords,
  timeRange,
  selectedProduct,
  stockUpdates,
  filteredUsageRecords,
  filteredStockUpdates,
}) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - (timeRange === "7days" ? 7 : timeRange === "30days" ? 30 : 90));

  const productUsageData = prepareProductUsageData(selectedProduct?.id || '', startDate, filteredUsageRecords);
  const stockLevelHistory = prepareStockLevelHistory(selectedProduct, startDate, usageRecords, stockUpdates);
  const productStats = calculateProductStats(selectedProduct, filteredUsageRecords, filteredStockUpdates);
  const totalStats = calculateTotalStats(products, filteredUsageRecords, filteredStockUpdates);

  return (
    <>
      {selectedGroupId !== "all-groups" && (
        <GroupAnalytics
          selectedGroupId={selectedGroupId}
          groups={groups}
          products={products}
          usageRecords={filteredUsageRecords}
          timeRange={timeRange}
        />
      )}

      <ProductStats
        selectedProduct={selectedProduct}
        productStats={productStats}
        timeRange={timeRange}
      />

      <OverallSummary
        totalUsage={totalStats.totalUsage}
        totalStockAdded={totalStats.totalStockAdded}
        lowStockProducts={totalStats.lowStockProducts}
      />

      <ProductCharts
        selectedProduct={selectedProduct}
        productUsageData={productUsageData}
        stockLevelHistory={stockLevelHistory}
      />

      <ProductComparison
        products={products}
        filteredUsageRecords={filteredUsageRecords}
        filteredStockUpdates={filteredStockUpdates}
      />
    </>
  );
};

export default AnalyticsContent;
